import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Clock, RotateCcw } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/Card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import useAppStore from '@/store/useAppStore'
import useGetCurrentUser from '@/services/api/auth/useGetCurrentUser'
import {
  useConfirmSpaceReset,
  useRequestSpaceReset,
  useSpaceResetPreview,
} from '@/services/api/team/useSpaceReset'
import {
  RESET_ACKNOWLEDGEMENT,
  formatCountdown,
  plural,
} from './spaceResetCopy'

/** Queries kept across a reset: who the user is and which spaces they have. */
const KEEP_QUERIES = ['teams', 'current-user', 'team-details']

type Step = 'preview' | 'confirm'

/** Server message for 4xx; never surface a 5xx exception text. */
function errorMessage(error: any, fallback: string) {
  const status = error?.response?.status ?? 500
  return status < 500 ? (error?.response?.data?.message ?? fallback) : fallback
}

/**
 * Danger zone → Reset space data (14zfmu11ywd). Owner only; the parent decides
 * whether to render it. Two steps: what will happen (live counts), then the
 * emailed code + acknowledgement.
 */
export default function ResetSpaceDataSection({
  team,
}: {
  team: { id: number | string; name: string }
}) {
  const [open, setOpen] = useState(false)

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold uppercase tracking-wide text-red-800">
        Danger zone
      </h4>
      <Card className="w-full border-red-300 bg-red-50 p-6">
        <div className="space-y-4">
          <div>
            <h4 className="font-medium text-red-900">Reset space data</h4>
            <p className="mt-1 text-sm text-red-700">
              Permanently delete every transaction, recurring series, Money Path
              and model in this space. Vaults, categories, settings and members
              are kept; opening balances restart at $0.
            </p>
          </div>
          <Button variant="destructive" size="sm" onClick={() => setOpen(true)}>
            <RotateCcw className="mr-2 h-4 w-4" />
            Reset space data
          </Button>
        </div>
      </Card>

      {open && (
        <ResetSpaceDataDialog team={team} onClose={() => setOpen(false)} />
      )}
    </div>
  )
}

function ResetSpaceDataDialog({
  team,
  onClose,
}: {
  team: { id: number | string; name: string }
  onClose: () => void
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const setBannerTeamId = useAppStore((s) => s.setSpaceResetBannerTeamId)
  const { data: currentUser } = useGetCurrentUser()

  const [step, setStep] = useState<Step>('preview')
  const [pending, setPending] = useState<{
    auditLogId: string
    expiresAt: number
  } | null>(null)
  const [code, setCode] = useState('')
  const [acknowledged, setAcknowledged] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [now, setNow] = useState(() => Date.now())

  const preview = useSpaceResetPreview(team.id, step === 'preview')
  const requestReset = useRequestSpaceReset(team.id)
  const confirmReset = useConfirmSpaceReset(team.id)

  const secondsLeft = pending
    ? Math.max(0, Math.ceil((pending.expiresAt - now) / 1000))
    : 0
  const expired = step === 'confirm' && secondsLeft === 0

  useEffect(() => {
    if (step !== 'confirm') return
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [step])

  async function sendCode() {
    setError(null)
    try {
      const res = await requestReset.mutateAsync()
      setPending({
        auditLogId: res.audit_log_id,
        expiresAt: new Date(res.expires_at).getTime(),
      })
      setNow(Date.now())
      setCode('')
      setStep('confirm')
    } catch (e: any) {
      setError(
        e?.response?.status === 429
          ? errorMessage(e, 'Too many codes requested. Please try again later.')
          : errorMessage(e, 'Could not send the code. Please try again.'),
      )
    }
  }

  async function submit() {
    if (!pending) return
    setError(null)
    try {
      await confirmReset.mutateAsync({
        audit_log_id: pending.auditLogId,
        code,
        acknowledgement_version: RESET_ACKNOWLEDGEMENT.version,
      })
      onSuccess()
    } catch (e: any) {
      const status = e?.response?.status
      if (status === 422) {
        const left = e?.response?.data?.attempts_remaining
        setError(
          left !== undefined
            ? `The code is incorrect. ${plural(left, 'attempt')} left.`
            : errorMessage(e, 'Please check the code and the confirmation.'),
        )
      } else if (status === 410) {
        // Expired or burned: start again from step 1.
        setPending(null)
        setCode('')
        setStep('preview')
        setError(errorMessage(e, 'This code has expired. Please start again.'))
      } else if (status === 500) {
        setError('Reset failed — nothing was deleted.')
      } else {
        setError(errorMessage(e, 'Something went wrong. Please try again.'))
      }
    }
  }

  function onSuccess() {
    // Nothing from before the reset may be shown again: drop every cached
    // query not on screen, refetch the rest.
    queryClient.removeQueries({
      type: 'inactive',
      predicate: (q) => !KEEP_QUERIES.includes(String(q.queryKey[0])),
    })
    queryClient.invalidateQueries()
    setBannerTeamId(team.id)
    toast.success('Space data reset')
    onClose()
    navigate('/table')
  }

  const codeSent = step === 'confirm'
  const canSubmit =
    codeSent &&
    code.length === 6 &&
    acknowledged &&
    !expired &&
    !confirmReset.isPending

  const counts = preview.data

  return (
    <Dialog open onOpenChange={(next) => !next && !codeSent && onClose()}>
      <DialogContent
        // Fixed, not page-absolute: the Danger zone sits at the bottom of a
        // long page, and an absolute dialog would open off-screen above it.
        absolute={false}
        className="max-h-[90dvh] w-[calc(100vw-2rem)] overflow-y-auto md:max-w-lg"
        // Once the code is out, only an explicit Cancel closes the modal.
        onInteractOutside={(e) => codeSent && e.preventDefault()}
        onEscapeKeyDown={(e) => codeSent && e.preventDefault()}
      >
        <DialogHeader className="flex-col items-start">
          <DialogTitle className="flex items-center gap-2 text-red-900">
            <AlertTriangle className="h-5 w-5" />
            Reset space data — {team.name}
          </DialogTitle>
          <DialogDescription>
            {codeSent
              ? 'Confirm with the code we emailed you.'
              : 'Check what will happen before you continue.'}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {error && (
            <p
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
            >
              {error}
            </p>
          )}

          {!codeSent && (
            <>
              {preview.isPending && (
                <p className="text-sm text-txt-secondary">Counting…</p>
              )}
              {preview.isError && (
                <p className="text-sm text-red-700">
                  {errorMessage(preview.error, 'Could not load the summary.')}
                </p>
              )}
              {counts && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <p className="mb-1 text-sm font-medium text-red-900">
                      Will be permanently deleted
                    </p>
                    <ul className="list-inside list-disc text-sm text-txt-secondary">
                      <li>{plural(counts.delete.transactions, 'transaction')}</li>
                      <li>
                        {plural(counts.delete.recurring_series, 'recurring series', 'recurring series')}
                      </li>
                      <li>{plural(counts.delete.money_paths, 'Money Path')}</li>
                      <li>{plural(counts.delete.models, 'model')}</li>
                    </ul>
                  </div>
                  <div>
                    <p className="mb-1 text-sm font-medium text-txt-primary">
                      Will be kept
                    </p>
                    <ul className="list-inside list-disc text-sm text-txt-secondary">
                      <li>
                        {plural(counts.keep.vaults, 'vault')} (opening balances
                        reset to $0)
                      </li>
                      <li>{plural(counts.keep.categories, 'category', 'categories')}</li>
                      <li>Settings and members</li>
                    </ul>
                  </div>
                </div>
              )}
            </>
          )}

          {codeSent && (
            <>
              <p className="text-sm text-txt-secondary">
                We&apos;ve emailed a code to{' '}
                <span className="font-medium text-txt-primary">
                  {currentUser?.email ?? 'your email address'}
                </span>
                . It expires in 10 minutes.
              </p>

              <div>
                <Label htmlFor="reset-code">Verification Code</Label>
                <Input
                  id="reset-code"
                  value={code}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  onChange={(e) =>
                    setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  placeholder="Enter 6-digit code"
                  className="text-center text-lg tracking-widest"
                  maxLength={6}
                />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <span
                  className={`flex items-center gap-2 ${expired ? 'text-red-700' : 'text-gray-500'}`}
                >
                  <Clock className="h-4 w-4" />
                  {expired
                    ? 'The code has expired.'
                    : `Time remaining: ${formatCountdown(secondsLeft)}`}
                </span>
                <Button
                  type="button"
                  variant="link"
                  className="h-auto p-0"
                  disabled={requestReset.isPending}
                  onClick={sendCode}
                >
                  Resend code
                </Button>
              </div>

              <label className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
                <Checkbox
                  checked={acknowledged}
                  onCheckedChange={(v) => setAcknowledged(v === true)}
                  className="mt-0.5 shrink-0"
                  aria-label="Acknowledge permanent deletion"
                />
                <span>{RESET_ACKNOWLEDGEMENT.text}</span>
              </label>
            </>
          )}
        </DialogBody>

        <DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={confirmReset.isPending}
          >
            Cancel
          </Button>
          {!codeSent ? (
            <Button
              type="button"
              variant="destructive"
              disabled={!counts || requestReset.isPending}
              loading={requestReset.isPending}
              onClick={sendCode}
            >
              Send confirmation code
            </Button>
          ) : (
            <Button
              type="button"
              variant="destructive"
              disabled={!canSubmit}
              loading={confirmReset.isPending}
              onClick={submit}
            >
              Reset space data
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
